import { useState, type FormEvent, type ReactNode } from 'react'
import { Button, Heading, Stack, Text } from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { Pinput } from '@/shared/components/Pinput'
import { ErrorMessage } from '@/shared/components/ErrorMessage'
import styles from './OtpForm.module.css'

type OtpFormProps = {
  title: string
  description?: ReactNode
  /** Accessible label for the code field (becomes each cell's name). */
  inputLabel: string
  value: string
  onChange: (next: string) => void
  /** Called only once the code is complete (`value.length === length`). */
  onSubmit: () => void
  submitLabel: string
  length?: number
  isSubmitting?: boolean
  /** Server/validation error to surface; falsy values render nothing. */
  error?: ReactNode
  errorId?: string
  mask?: boolean
  secondaryLabel?: string
  onSecondary?: () => void
  autoFocus?: boolean
}

export const OtpForm = ({
  title,
  description,
  inputLabel,
  value,
  onChange,
  onSubmit,
  submitLabel,
  length = 6,
  isSubmitting = false,
  error,
  errorId = 'otp-error',
  mask = false,
  secondaryLabel,
  onSecondary,
  autoFocus = true,
}: OtpFormProps) => {
  const { t } = useTranslation()
  const [showIncomplete, setShowIncomplete] = useState(false)
  const isComplete = value.length === length

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!isComplete) {
      setShowIncomplete(true)
      return
    }
    setShowIncomplete(false)
    onSubmit()
  }

  return (
    <form onSubmit={handleSubmit} noValidate className={styles.form}>
      <Stack direction='vertical' space='16'>
        <Heading level={2}>{title}</Heading>
        {description ? (
          <Text variant='small' color='neutral-alternate'>
            {description}
          </Text>
        ) : null}

        <Pinput
          length={length}
          value={value}
          onChange={onChange}
          disabled={isSubmitting}
          label={inputLabel}
          mask={mask}
          // The code field is the primary action on this screen, so focusing it
          // on mount mirrors slim's existing flow.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />

        {error ? <ErrorMessage id={errorId}>{error}</ErrorMessage> : null}
        {!error && showIncomplete && !isComplete ? (
          <ErrorMessage id={`${errorId}-incomplete`}>
            {t('otp.incomplete', { count: length })}
          </ErrorMessage>
        ) : null}

        <Button
          type='submit'
          variant='primary'
          color='teal'
          loading={isSubmitting}
        >
          {submitLabel}
        </Button>

        {secondaryLabel && onSecondary ? (
          <Button
            type='button'
            variant='tertiary'
            color='teal'
            onClick={onSecondary}
            disabled={isSubmitting}
          >
            {secondaryLabel}
          </Button>
        ) : null}
      </Stack>
    </form>
  )
}
